import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Phone, Mail, Globe, Flower2, BookOpen, Coffee, Home, Brain, Sparkles, Compass, Shield, MessageCircle, Target, Bed, Apple } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import teensImageWebP from '@/assets/teens.webp';
import teensImage from '@/assets/teens.jpg';

const TeensPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Chinese Medicine for Teens Sunshine Coast | Hormone Balance & Wellness";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Supporting balance, confidence, and vitality for teens. Gentle Chinese medicine care for periods, stress, acne, and energy in Currimundi.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Chinese Medicine for Teens Sunshine Coast | Hormone Balance & Wellness");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Supporting balance, confidence, and vitality for teens. Gentle Chinese medicine care for periods, stress, acne, and energy in Currimundi.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/teens");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/teens.webp");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Chinese Medicine for Teens Sunshine Coast | Hormone Balance & Wellness");
    }

    // Add LocalBusiness Schema for teen services
    const teensSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Chinese Medicine for Teens",
      "image": "https://womenandtheway.com.au/teens.webp",
      "description": "Supporting balance, confidence, and vitality for teens. Gentle Chinese medicine care for periods, stress, acne, and energy in Currimundi.",
      "url": "https://womenandtheway.com.au/teens",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Teen Health, Acupuncture, Chinese Medicine, Hormone Balance",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(teensSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                  Chinese Medicine for Teens
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Supporting balance, confidence, and vitality - one treatment at a time.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  The teenage years are a time of incredible growth and transformation. Physically, emotionally, and energetically, your teen is navigating a lot. At Women and the Way, we offer gentle and empowering Chinese medicine care to support them through the ups and downs - whether it's managing periods, stress, acne, or energy slumps.
                </p>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : 'Book Now'}</span>
                  <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
                </Button>
              </div>
              <div className="relative">
                <picture>
                  <source srcSet={teensImageWebP} type="image/webp" />
                  <img 
                    src={teensImage} 
                    alt="Teen receiving gentle acupuncture for hormone balance and wellness" 
                    className="rounded-2xl shadow-lg w-full h-auto"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Why Teen Health Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 flex items-center justify-center gap-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                <Sparkles className="w-8 h-8 text-sage-600" />
                Why Teen Health Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Chinese medicine, the teenage years are governed by the blossoming of Yin and Yang - two dynamic forces that shape development. Hormones surge, emotions intensify, and the body begins to take on its adult form. But this process can also lead to imbalance: irregular periods, breakouts, fatigue, anxiety, and mood swings.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Rather than simply 'waiting it out', we believe in early, gentle intervention - creating a foundation of balance that lasts a lifetime.
              </p>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Support for Teens In-Clinic or Online
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Visit our peaceful Currimundi clinic for sessions designed specifically for teens - gentle, safe, and tailored to their comfort and needs.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Gentle acupuncture to regulate hormones, reduce anxiety, and restore energy</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Chinese herbal medicine for skin health, mood support, digestion, and period regulation</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🧠</span>
                      <p className="text-sage-700">Emotional regulation techniques, breathwork, and mindset tools for stress and overwhelm</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📚</span>
                      <p className="text-sage-700">Cycle education and body literacy tools to build confidence and awareness</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Care</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    For busy schedules, shy teens, or regional families - we offer online support too.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💬</span>
                      <p className="text-sage-700">Telehealth consults to discuss symptoms, concerns, or emotional challenges in a relaxed setting</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Custom Chinese herbal prescriptions posted to your door or collected at clinic</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📖</span>
                      <p className="text-sage-700">Simple guidance and routines for nervous system health, period care, and emotional regulation</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-sage-50 rounded-xl p-6 text-center">
              <p className="text-lg text-sage-700 mb-4">
                <strong>We recommend monthly sessions or seasonal check-ins during key stages of development.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Teen Wellness Timeline */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                The Teen Wellness Timeline
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Flower2 className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Ages 10-13: Hormonal awakening & emotional regulation</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 font-medium">✨ The focus here is gentle support for emerging changes.</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sage-800">What we support:</h4>
                        <ul className="space-y-1 text-sage-700">
                          <li>• Early hormonal shifts</li>
                          <li>• Emotional regulation skills</li>
                          <li>• Body awareness and confidence</li>
                          <li>• Stress management tools</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sage-800">Common areas of support:</h4>
                        <ul className="space-y-1 text-sage-700">
                          <li>• Mood swings and anxiety</li>
                          <li>• Sleep disturbances</li>
                          <li>• Early acne or skin changes</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Ages 14-17: Period establishment, energy support, skin health</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700">
                      This stage brings more significant hormonal changes as the menstrual cycle establishes and the body continues developing.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sage-800">Common symptoms:</h4>
                        <ul className="space-y-1 text-sage-700">
                          <li>• Irregular or painful periods</li>
                          <li>• Acne and skin inflammation</li>
                          <li>• Energy fluctuations</li>
                          <li>• Digestive changes</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sage-800">Our care focuses on:</h4>
                        <ul className="space-y-1 text-sage-700">
                          <li>• Regulating menstrual cycles</li>
                          <li>• Supporting skin health</li>
                          <li>• Balancing energy levels</li>
                          <li>• Building body confidence</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Ages 18-20: Stress, study, identity, and preconception awareness</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700">
                      As teens approach adulthood, they face new challenges with study, relationships, and identity formation.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sage-800">Common challenges:</h4>
                        <ul className="space-y-1 text-sage-700">
                          <li>• Study stress and overwhelm</li>
                          <li>• Sleep issues</li>
                          <li>• Relationship dynamics</li>
                          <li>• Future planning anxiety</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sage-800">Our care focuses on:</h4>
                        <ul className="space-y-1 text-sage-700">
                          <li>• Stress management techniques</li>
                          <li>• Sleep hygiene and support</li>
                          <li>• Emotional resilience building</li>
                          <li>• Preconception health awareness</li>
                        </ul>
                      </div>
                    </div>
                    <p className="text-sage-700 font-medium">✨ Building a foundation for lifelong wellness.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Conditions We Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Conditions We Commonly Support
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Acne & skin inflammation", chinese: "Heat in Lung/Stomach channels, Blood Heat" },
                { western: "Anxiety & panic attacks", chinese: "Heart Yin deficiency, Liver Qi stagnation" },
                { western: "Period pain & irregular cycles", chinese: "Liver Qi stagnation, Blood deficiency" },
                { western: "PMS & mood swings", chinese: "Liver Yang rising, Spleen Qi deficiency" },
                { western: "Low energy & fatigue", chinese: "Qi & Blood deficiency, Spleen Qi deficiency" },
                { western: "Digestive complaints", chinese: "Spleen/Stomach imbalance, Damp accumulation" },
                { western: "Study stress & overwhelm", chinese: "Heart/Spleen deficiency, Liver Qi constraint" },
                { western: "Sleep issues", chinese: "Heart Yin deficiency, Liver Fire, Spleen overload" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What Makes Our Approach Different
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "Personalised & teen-centred", desc: "We meet each teen where they're at - with empathy, curiosity, and a non-judgmental approach" },
                { icon: "🌿", title: "Natural tools that work", desc: "We prioritise gentle, time-tested treatments with minimal side effects" },
                { icon: "📚", title: "Education is everything", desc: "We empower teens with knowledge about their body, cycle, and emotional patterns" },
                { icon: "👨‍👩‍👧", title: "Parent-informed, teen-led", desc: "We keep lines of communication open while allowing your teen to feel ownership over their care" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Support Tools */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Support Tools We Offer in Clinic
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Leaf, title: "Custom herbal formulas", desc: "For mood, skin, digestion, and cycles" },
                { icon: Coffee, title: "Seed cycling blends", desc: "Hormone-balancing teas and supplements" },
                { icon: Heart, title: "Acne-support tinctures", desc: "And dietary advice for clear skin" },
                { icon: BookOpen, title: "Castor oil packs", desc: "With lifestyle how-tos for home use" },
                { icon: BookOpen, title: "Recommended reads", desc: "And cycle journals for body awareness" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book a Teen Wellness Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Whether they're just starting their period or navigating exams and identity shifts - Chinese medicine has a gentle, effective answer. Let's support their body, mind, and spirit to thrive.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
              onClick={handleBooking}
              disabled={isLoading}
            >
              <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : 'Book Teen Wellness Session'}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Button>
          </div>
        </section>

        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default TeensPage; 